import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BroadcastMessageModel, CashflowSubgroupModel, GenericResModel } from 'countable@model';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ScheduleUtil } from '../helpers/schedule/schedule-util';
import { HeaderNameModel } from '../model/workbook/header-name.model';
import { WbNoteModel } from '../model/workbook/wb-note.model';

@Injectable({
  providedIn: 'root'
})
export class WorkbookService {
  scheduleUtil: typeof ScheduleUtil = ScheduleUtil;
  // workbook window events into inside an eng window
  private isBroadcastChannelSupported: boolean = typeof BroadcastChannel !== 'undefined';
  private static readonly CHANNEL_NAME = 'workbook';
  private workbookChannel: BroadcastChannel | null = null;
  private tabId = this.scheduleUtil.getOrCreateTabId(`${WorkbookService.CHANNEL_NAME}-broadcast-tab-id`);  // Unique ID per tab
  private messageSubject = new Subject<BroadcastMessageModel>();
  public message$ = this.messageSubject.asObservable();

  constructor(private http: HttpClient) {}

  initChannel(): void {
    if(!this.workbookChannel && this.isBroadcastChannelSupported){
      this.workbookChannel = new BroadcastChannel(WorkbookService.CHANNEL_NAME);

      this.workbookChannel.onmessage = (event) => {
        const data = event.data;
        (data.sender == this.tabId) &&  this.messageSubject.next(data);
      };
    }
  }

  sendMessage(message: Partial<BroadcastMessageModel>): void {
    if(this.isBroadcastChannelSupported){
      this.initChannel();
      this.workbookChannel.postMessage({
        sender: this.tabId,
        ...message,
      });
    } else{
      localStorage.setItem('schedule_refresh', JSON.stringify(message));
      setTimeout(() => localStorage.removeItem('schedule_refresh'), 0);
    }
  }

  closeChannel(): void {
    if (this.workbookChannel) {
      this.workbookChannel.onmessage = null;
      this.workbookChannel.close();
      this.workbookChannel = null;
    }
  }

  updateSortAndRename(type: string, data: CashflowSubgroupModel[]): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(environment.apiV1 + '/cashflow-subgroup?action=' + type, data);
  }

  getWbHeaderNote(engId: number, subModuleId: number | string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/wb-header-note', {
      params: {
        'engId': engId,
        'subModuleId': (subModuleId && subModuleId != 'null' && subModuleId != '' ? subModuleId : null)
      }
    });
  }

  getWbLeftSide(engId: number): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/wb-left-side', {params: {'engId': engId}});
  }

  saveChangeWbHeaderName(headerNameModel: HeaderNameModel): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(environment.apiV1 + '/wb-header-name-change', headerNameModel);
  }

  saveWbNote(wbNoteModel: WbNoteModel): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(environment.apiV1 + '/wb-note', wbNoteModel);
  }

  saveOrderChange(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(environment.apiV1 + '/wb-order-change', data);
  }

  saveWbLeftSideNameChange(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(environment.apiV1 + '/wb-left-side-name-change', data);
  }

  saveWbCreateGroup(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(environment.apiV1 + '/wb-create-group', data);
  }

  saveWbAddItem(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(environment.apiV1 + '/wb-add-item', data);
  }

  deleteWbGroupItem(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(environment.apiV1 + '/wb-group-item', {params: {'data': data}});
  }
}
